import { useState, useEffect, useCallback } from 'react';
import { petApi, type Pet } from '../services/api';

interface UsePetsReturn {
  pets: Pet[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addPet: (data: Parameters<typeof petApi.create>[0]) => Promise<Pet>;
  updatePet: (id: string, data: Parameters<typeof petApi.update>[1]) => Promise<Pet>;
  deletePet: (id: string) => Promise<void>;
}

export function usePets(): UsePetsReturn {
  const [pets, setPets] = useState<Pet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await petApi.list();
      setPets(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPets();
  }, [fetchPets]);

  const addPet = useCallback(async (data: Parameters<typeof petApi.create>[0]) => {
    const newPet = await petApi.create(data);
    setPets(prev => [newPet, ...prev]);
    return newPet;
  }, []);

  const updatePet = useCallback(async (id: string, data: Parameters<typeof petApi.update>[1]) => {
    const updated = await petApi.update(id, data);
    setPets(prev => prev.map(p => (p.id === id ? updated : p)));
    return updated;
  }, []);

  const deletePet = useCallback(async (id: string) => {
    await petApi.delete(id);
    setPets(prev => prev.filter(p => p.id !== id));
  }, []);

  return {
    pets,
    loading,
    error,
    refetch: fetchPets,
    addPet,
    updatePet,
    deletePet,
  };
}
